import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/Button';
import { mensagemDeErro } from '@/lib/format';
import { listarServicos } from '@/features/catalog/catalog.api';
import {
  apenasAtivos,
  listaDeImpactos,
  listaDeUrgencias,
  rotuloDaImpacto,
  rotuloDaUrgencia,
  subcategoriasDaCategoria,
  useOpcoesDeFormulario,
} from '@/features/tickets/opcoes';
import { atualizarChamado, chaves } from '@/features/tickets/tickets.api';
import { type Chamado } from '@/features/tickets/types';

const CAMPO =
  'w-full rounded-lg border border-input/80 bg-muted/40 px-2 py-1.5 text-sm ' +
  'transition-colors hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background ' +
  'focus-visible:outline-none focus-visible:shadow-glow ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Controle de classificação operacional do chamado.
 *
 * `defaultAberto` força o modo edição ao montar — usado quando o chamado
 * ainda não passou por triagem (sem equipe e status Novo).
 */
export function ClassificacaoControl({
  chamado,
  defaultAberto = false,
}: {
  chamado: Chamado;
  defaultAberto?: boolean;
}) {
  const clienteDeConsulta = useQueryClient();
  const [editando, setEditando] = useState(defaultAberto);
  const [teamId, setTeamId] = useState<string>(chamado.teamId ?? '');
  const [categoryId, setCategoryId] = useState<string>(chamado.categoryId ?? '');
  const [subcategoryId, setSubcategoryId] = useState<string>(chamado.subcategoryId ?? '');
  const [serviceId, setServiceId] = useState<string>(chamado.serviceId ?? '');
  const [impact, setImpact] = useState(chamado.impact);
  const [urgency, setUrgency] = useState(chamado.urgency);

  const opcoes = useOpcoesDeFormulario();
  const servicos = useQuery({
    queryKey: ['catalog', 'servicos'],
    queryFn: () => listarServicos(),
    staleTime: 30 * 60_000,
    enabled: editando || Boolean(chamado.serviceId),
  });

  const salvar = useMutation({
    mutationFn: () =>
      atualizarChamado(chamado.id, {
        teamId: teamId || null,
        categoryId: categoryId || null,
        subcategoryId: subcategoryId || null,
        serviceId: serviceId || null,
        impact: impact,
        urgency: urgency,
        version: chamado.version,
      }),
    onSuccess: (atualizado) => {
      setEditando(false);
      void clienteDeConsulta.invalidateQueries({ queryKey: chaves.raiz });
      clienteDeConsulta.setQueryData(chaves.item(chamado.id), atualizado);
    },
  });

  function abrir() {
    setTeamId(chamado.teamId ?? '');
    setCategoryId(chamado.categoryId ?? '');
    setSubcategoryId(chamado.subcategoryId ?? '');
    setServiceId(chamado.serviceId ?? '');
    setImpact(chamado.impact);
    setUrgency(chamado.urgency);
    setEditando(true);
  }

  const subcategorias = subcategoriasDaCategoria(opcoes.data, categoryId || null);
  const equipes = apenasAtivos(opcoes.data?.teams ?? []);
  const categorias = apenasAtivos(opcoes.data?.categories ?? []);
  const servicosAtivos = (servicos.data ?? []).filter((s) => s.active && s.portfolioStage === 'CATALOGUE');

  return (
    <div className="space-y-3 rounded-xl border border-border bg-card px-4 py-3 shadow-card">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-foreground">Classificação</p>
        {!editando ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={abrir}
            icon={<Pencil className="h-3.5 w-3.5" aria-hidden />}
          >
            Editar
          </Button>
        ) : null}
      </div>

      {!editando ? (
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
          <Entrada termo="Equipe" valor={nomeOuVazio(opcoes.data?.teams, chamado.teamId, 'sem equipe')} />
          <Entrada termo="Serviço" valor={nomeOuVazio(servicos.data, chamado.serviceId, 'sem serviço')} />
          <Entrada termo="Categoria" valor={nomeOuVazio(opcoes.data?.categories, chamado.categoryId, 'sem categoria')} />
          <Entrada termo="Subcategoria" valor={nomeOuVazio(opcoes.data?.subcategories, chamado.subcategoryId, '—')} />
          <Entrada termo="Impacto" valor={rotuloDaImpacto(opcoes.data, chamado.impact)} />
          <Entrada termo="Urgência" valor={rotuloDaUrgencia(opcoes.data, chamado.urgency)} />
        </dl>
      ) : (
        <form
          className="space-y-2.5"
          onSubmit={(e) => {
            e.preventDefault();
            salvar.mutate();
          }}
        >
          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Equipe</span>
            <select value={teamId} onChange={(e) => setTeamId(e.target.value)} className={CAMPO}>
              <option value="">— sem equipe —</option>
              {equipes.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>

          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Serviço</span>
            <select
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
              className={CAMPO}
              disabled={servicos.isPending}
            >
              <option value="">— sem serviço —</option>
              {servicosAtivos.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>

          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Categoria</span>
            <select
              value={categoryId}
              onChange={(e) => {
                setCategoryId(e.target.value);
                setSubcategoryId('');
              }}
              className={CAMPO}
            >
              <option value="">— sem categoria —</option>
              {categorias.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>

          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Subcategoria</span>
            <select
              value={subcategoryId}
              onChange={(e) => setSubcategoryId(e.target.value)}
              disabled={subcategorias.length === 0}
              className={CAMPO}
            >
              <option value="">— sem subcategoria —</option>
              {subcategorias.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-2 gap-2">
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Impacto</span>
              <select
                value={impact}
                onChange={(e) => setImpact(e.target.value as typeof impact)}
                className={CAMPO}
              >
                {listaDeImpactos(opcoes.data).filter((i) => i.active).map((i) => (
                  <option key={i.level} value={i.level}>{i.label}</option>
                ))}
              </select>
            </label>

            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Urgência</span>
              <select
                value={urgency}
                onChange={(e) => setUrgency(e.target.value as typeof urgency)}
                className={CAMPO}
              >
                {listaDeUrgencias(opcoes.data).filter((u) => u.active).map((u) => (
                  <option key={u.level} value={u.level}>{u.label}</option>
                ))}
              </select>
            </label>
          </div>

          {salvar.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {mensagemDeErro(salvar.error)}
            </p>
          ) : null}

          <div className="flex gap-2 pt-1">
            <Button type="submit" size="sm" loading={salvar.isPending}>
              Salvar
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setEditando(false)}
              disabled={salvar.isPending}
            >
              Cancelar
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function nomeOuVazio(
  lista: ReadonlyArray<{ id: string; name: string }> | undefined,
  id: string | null | undefined,
  fallback: string,
): string {
  if (!id) return fallback;
  return lista?.find((item) => item.id === id)?.name ?? fallback;
}

function Entrada({ termo, valor }: { termo: string; valor: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{termo}</dt>
      <dd className="mt-0.5 truncate text-xs font-medium">{valor}</dd>
    </div>
  );
}
